import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import DualChatView from '@/components/Chat/DualChatView';
import { useSessionStore } from '@/stores/useSessionStore';
import { useChatStore } from '@/stores/useChatStore';
import { useConfigStore } from '@/stores/useConfigStore';
import { getSession } from '@/api/sessions';
import apiClient from '@/api/client';
import { useAuthStore } from '@/stores/useAuthStore';
import { hasPermission } from '@/utils/permissions';

/**
 * 对话主页面。
 * 整合 DualChatView，首次进入无会话时自动创建新会话。
 */
const ChatPage: React.FC = () => {
  const sessions = useSessionStore((s) => s.sessions);
  const currentSession = useSessionStore((s) => s.currentSession);
  const loadingSessions = useSessionStore((s) => s.loading);
  const loadedSessions = useSessionStore((s) => s.loaded);
  const createSession = useSessionStore((s) => s.createSession);
  const loadSessions = useSessionStore((s) => s.loadSessions);
  const clearMessages = useChatStore((s) => s.clearMessages);
  const setMessages = useChatStore((s) => s.setMessages);
  const setError = useChatStore((s) => s.setError);
  const loadConfig = useConfigStore((s) => s.loadConfig);
  const permissions = useAuthStore((s) => s.permissions);
  const permissionVersion = useAuthStore((s) => s.permissionVersion);
  const canConfigure = hasPermission(permissions, 'config');
  const canWrite = hasPermission(permissions, 'conversations', 'write');

  // 进入聊天页时加载后端生产配置，避免显示或使用前端默认模型。
  useEffect(() => {
    if (canConfigure) {
      loadConfig();
      return;
    }
    let cancelled = false;
    apiClient.get('/chat/models').then(({ data }) => {
      if (cancelled) return;
      const state = useConfigStore.getInitialState();
      useConfigStore.setState({
        leftConfig: { ...state.leftConfig, ...data.left },
        rightConfig: { ...state.rightConfig, ...data.right },
      });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [loadConfig, canConfigure, permissionVersion]);

  // 首次加载会话列表
  useEffect(() => {
    loadSessions();
  }, [loadSessions, permissionVersion]);

  // 无会话时自动创建
  useEffect(() => {
    if (canWrite && loadedSessions && !loadingSessions && sessions.length === 0 && !currentSession) {
      createSession('新会话');
    }
  }, [sessions, currentSession, loadingSessions, loadedSessions, createSession, canWrite]);

  // 切换会话时加载历史消息
  useEffect(() => {
    let cancelled = false;
    const sessionId = currentSession?.session_id;
    if (!sessionId) {
      clearMessages();
      return;
    }

    getSession(sessionId)
      .then((session) => {
        if (!cancelled) {
          useSessionStore.getState().setCurrentSession(session);
          setMessages(session.messages || []);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          clearMessages();
          setError(err instanceof Error ? err.message : '加载历史消息失败');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [currentSession?.session_id, permissionVersion, clearMessages, setMessages, setError]);

  return (
    <Box sx={{ height: '100%', overflow: 'hidden' }}>
      <DualChatView />
    </Box>
  );
};

export default ChatPage;
